// "Read with Claude": Claude reads the claim PDF/photo into a DRAFT (lines, variations, totals). A PM looks it over and
// chooses what to use; everything used is marked "drafted by Claude … confirmed by <PM>" in the history.
import { useEffect, useState } from 'react';
import { api, write } from '../../lib/api';
import { supabase } from '../../lib/supabase';
import { useLive } from '../../lib/live';
import { money } from '../../lib/claimCalc';
import { fmtDateTime } from '../../lib/format';
import type { ClaimDetails, ClaimLine } from '../../lib/claimData';
import type { FileRow } from '../ItemFiles';
import { Button, errText, useToast } from '../kit';

type Draft = {
  claim_no: number | null;
  period_to: string | null;
  contractor_total_vip: number | null;
  works_this_period_vep: number | null;
  variations_this_period_vep: number | null;
  lines: { code: string | null; description: string; contract_value: number | null; prev_pct: number | null; claimed_pct: number | null; this_claim_amount: number | null }[];
  variations: { ref: string | null; description: string | null; approved_amount: number | null; this_claim_amount: number | null; listed_as_approved: boolean | null }[];
  notes: string | null;
};
type Call = { id: string; phase: string; status: string; error: string | null; output: Draft | null; created_at: string; finished_at: string | null; file_ids: string[]; confirmed_at: string | null; model: string | null };

const READABLE = /^(application\/pdf|image\/(jpeg|png|webp|gif))$/i;

export function ClaudeRead({ itemId, files, details, lines, varCount, onChanged }: { itemId: string; files: FileRow[]; details: ClaimDetails; lines: ClaimLine[]; varCount: number; onChanged: () => void }) {
  const toast = useToast();
  const docs = files.filter((f) => f.is_current && f.kind === 'file' && READABLE.test(f.mime ?? '') && !f.comment_id);
  const [fileId, setFileId] = useState<string>('');
  const [call, setCall] = useState<Call | null>(null);
  const [busy, setBusy] = useState(false);
  const [replace, setReplace] = useState(true);

  useEffect(() => {
    if (!fileId && docs.length) setFileId(docs.find((d) => /pdf/i.test(d.mime ?? ''))?.id ?? docs[0].id);
  }, [docs, fileId]);

  const loadLatest = async () => {
    const { data } = await supabase.from('ai_calls').select('id, phase, status, error, output, created_at, finished_at, file_ids, confirmed_at, model').eq('item_id', itemId).eq('purpose', 'claim_read').order('created_at', { ascending: false }).limit(1);
    setCall(((data ?? [])[0] as Call) ?? null);
  };
  useEffect(() => void loadLatest(), [itemId]); // eslint-disable-line react-hooks/exhaustive-deps
  useLive(['ai_calls'], () => void loadLatest(), { filter: `item_id=eq.${itemId}` });
  // fallback poll while running (in case live updates are off)
  useEffect(() => {
    if (call?.phase !== 'running') return;
    const t = setInterval(() => void loadLatest(), 4000);
    return () => clearInterval(t);
  }, [call?.phase]); // eslint-disable-line react-hooks/exhaustive-deps

  async function start() {
    setBusy(true);
    try {
      await api('claim-read', { item_id: itemId, file_id: fileId });
      await loadLatest();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setBusy(false);
    }
  }

  const fileName = (id: string | undefined) => files.find((f) => f.id === id)?.original_name ?? 'the claim';
  const tag = (c: Call) => `Drafted by Claude from ${fileName(c.file_ids[0])} (${fmtDateTime(c.created_at)})`;

  async function useLines(c: Call) {
    const d = c.output!;
    const batch = crypto.randomUUID();
    setBusy(true);
    try {
      if (replace) for (const l of lines) await write('claim_lines', 'delete', { id: l.id, batch_id: batch, summary: 'Replaced by Claude draft' });
      let sort = replace ? 0 : Math.max(0, ...lines.map((l) => l.sort)) + 1;
      for (const l of d.lines)
        await write('claim_lines', 'insert', {
          batch_id: batch,
          values: { item_id: itemId, sort: sort++, source: 'ai', code: l.code, description: l.description, contract_value: l.contract_value, prev_pct: l.prev_pct, claimed_pct: l.claimed_pct, swi_pct: l.claimed_pct },
          summary: `${tag(c)} — confirmed`,
        });
      await api('claim-action', { action: 'ai_confirm', ai_call_id: c.id });
      toast({ tone: 'ok', text: `${d.lines.length} lines added — SWI % starts equal to claimed; change what you don't agree with` });
      onChanged();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setBusy(false);
    }
  }
  async function useVars(c: Call) {
    const batch = crypto.randomUUID();
    setBusy(true);
    try {
      let sort = varCount;
      for (const v of c.output!.variations)
        await write('claim_variations', 'insert', {
          batch_id: batch,
          values: { item_id: itemId, sort: sort++, source: 'ai', ref_on_claim: v.ref, description: v.description, listed_amount: v.approved_amount, claimed_now: v.this_claim_amount },
          summary: `${tag(c)} — confirmed`,
        });
      await api('claim-action', { action: 'ai_confirm', ai_call_id: c.id });
      toast({ tone: 'ok', text: `${c.output!.variations.length} variations added — now checked against the register` });
      onChanged();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setBusy(false);
    }
  }
  async function useTotals(c: Call) {
    const d = c.output!;
    const values: Record<string, unknown> = { ai_read_at: new Date().toISOString() };
    if (d.claim_no !== null) values.claim_no = d.claim_no;
    if (d.period_to) values.period_to = d.period_to;
    if (d.contractor_total_vip !== null) values.pbs_total_vip = d.contractor_total_vip;
    if (!d.lines.length && d.works_this_period_vep !== null) values.works_claimed = d.works_this_period_vep;
    try {
      await write('claim_details', 'update', { id: details.id, values, summary: `${tag(c)} — claim no., period and PBS total confirmed` });
      await api('claim-action', { action: 'ai_confirm', ai_call_id: c.id });
      onChanged();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    }
  }

  const d = call?.phase === 'done' ? call.output : null;
  return (
    <div className="ai-box">
      <div className="ai-head">
        <span className="ai-badge" aria-hidden="true">
          ✦
        </span>
        <strong>Read the claim with Claude</strong>
        <span className="muted small">Claude reads the PDF or photo into a draft — nothing changes until you choose what to use.</span>
      </div>
      {docs.length ? (
        <div className="resp-row">
          <select value={fileId} onChange={(e) => setFileId(e.target.value)} aria-label="Claim file">
            {docs.map((f) => (
              <option key={f.id} value={f.id}>
                {f.original_name}
              </option>
            ))}
          </select>
          <Button kind="primary" onClick={() => void start()} disabled={busy || call?.phase === 'running' || !fileId}>
            {call?.phase === 'running' ? 'Claude is reading…' : 'Read with Claude'}
          </Button>
        </div>
      ) : (
        <p className="muted small">Upload the claim PDF (or a photo of it) to Files first. For an Excel breakdown use “Paste from Excel” on the lines.</p>
      )}
      {call?.phase === 'running' ? <div className="ai-run">Reading {fileName(call.file_ids[0])}… usually under a minute. You can keep working.</div> : null}
      {call?.phase === 'failed' ? <div className="error-box">Claude couldn’t read it: {call.error}</div> : null}
      {d ? (
        <div className="ai-draft">
          <div className="muted small">
            {tag(call!)}
            {call!.confirmed_at ? ' · used' : ' · not used yet'}
          </div>
          <dl className="facts">
            <div>
              <dt>Claim no.</dt>
              <dd>{d.claim_no ?? '—'}</dd>
            </div>
            <div>
              <dt>Period to</dt>
              <dd>{d.period_to ?? '—'}</dd>
            </div>
            <div>
              <dt>PBS total (VIP)</dt>
              <dd>{money(d.contractor_total_vip)}</dd>
            </div>
            <div>
              <dt>Works this period (VEP)</dt>
              <dd>{money(d.works_this_period_vep)}</dd>
            </div>
            <div>
              <dt>Variations this period</dt>
              <dd>{money(d.variations_this_period_vep)}</dd>
            </div>
            <div>
              <dt>Found</dt>
              <dd>
                {d.lines.length} lines · {d.variations.length} variations
              </dd>
            </div>
          </dl>
          {d.notes ? <div className="banner banner-warn">Claude noted: {d.notes}</div> : null}
          {d.lines.length ? (
            <details>
              <summary className="small">See the lines Claude read</summary>
              <div className="table-wrap">
                <table className="t">
                  <tbody>
                    {d.lines.slice(0, 200).map((l, i) => (
                      <tr key={i}>
                        <td className="num">{l.code}</td>
                        <td>{l.description}</td>
                        <td className="right">{money(l.contract_value)}</td>
                        <td className="right">{l.prev_pct ?? '—'}</td>
                        <td className="right">{l.claimed_pct ?? '—'}</td>
                        <td className="right">{money(l.this_claim_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          ) : null}
          <div className="resp-row">
            <Button onClick={() => void useTotals(call!)} disabled={busy}>
              Use claim no., period and total
            </Button>
            {d.lines.length ? (
              <>
                <Button onClick={() => void useLines(call!)} disabled={busy}>
                  Use {d.lines.length} lines
                </Button>
                {lines.length ? (
                  <label className="check small">
                    <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} /> replace the {lines.length} lines already here
                  </label>
                ) : null}
              </>
            ) : null}
            {d.variations.length ? (
              <Button onClick={() => void useVars(call!)} disabled={busy}>
                Use {d.variations.length} variations
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
